import { forwardRef, Ref, RefAttributes, SyntheticEvent, useId, useState, type JSX } from "react";

import { useTheme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import InputBase from "@mui/material/InputBase";
import { normalizeAccents } from "@simplyk/common";
import { FieldValues, Path, PathValue, useController } from "react-hook-form";

import { FormSearchableSelectFieldProps, FormSearchableSelectOption } from "./types";

import { rankOptionsByShortCode } from "@/common/helpers/rankOptionsByShortCode";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useValidators } from "@/common/hooks/useValidators";
import { FormField, getFormFieldProps } from "@/forms-design-system/FormField";
import { FormIcon } from "@/forms-design-system/FormIcon";
import { FormTypography } from "@/forms-design-system/FormTypography";
import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { ChevronDown } from "@/icons/outlined";

function FormSearchableSelectFieldInner<T extends FieldValues>(
  {
    control,
    name,
    options,
    rules,
    defaultValue,
    id,
    placeholder,
    onChange,
    onOpen,
    disableInjectingGlobalStyles,
    error,
    disabled,
    "data-test": dataTest,
    optionDataTest,
    ...rest
  }: FormSearchableSelectFieldProps<T>,
  ref: Ref<HTMLDivElement>
) {
  const theme = useTheme();
  const { t } = useTranslate();
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const validators = useValidators(rules);
  const [open, setOpen] = useState(false);
  const { field } = useController<T>({
    name,
    control,
    defaultValue: defaultValue as PathValue<T, Path<T>> | undefined,
    rules: validators,
  });

  if (!theme.palette.forms || !theme.palette.formsComponents?.global || !theme.palette.formsComponents?.selectField) {
    return <InvalidThemeErrorMessage />;
  }

  const tokens = theme.palette.formsComponents.selectField;
  const matchedOption = options.find((option) => option.value === field.value);
  // Like the Select this replaces, show a stored value with no matching option (e.g. a full state name) as is.
  const unmatchedOption =
    !matchedOption && field.value ? { label: String(field.value), value: String(field.value) } : undefined;
  const selectedOption = matchedOption ?? unmatchedOption ?? null;

  const selectValue = (value: string) => {
    field.onChange(value);
    onChange?.(value);
  };

  const inputStyles = {
    ...theme.typography.body2,
    width: "100%",
    color: tokens.textColor,
    backgroundColor: tokens.backgroundColor,
    border: `1px solid ${tokens.borderColor}`,
    borderRadius: theme.spacing(1),
    transition: "outline 100ms ease-out, border-color 200ms ease-out",
    outlineColor: "transparent",
    ["&.Mui-error"]: {
      borderColor: tokens.errorBorderColor,
    },
    ["&:hover:not(.Mui-disabled)"]: {
      borderColor: tokens.hoverBorderColor,
    },
    ["&.Mui-focused"]: {
      borderColor: tokens.hoverBorderColor,
      outline: `3px solid ${theme.palette.formsComponents.global.focusOutlineColor}`,
    },
    ["&.Mui-disabled"]: {
      opacity: 0.4,
    },
    ["& .MuiInputBase-input"]: {
      minHeight: "28px",
      padding: theme.spacing(0.5, 0, 0.5, 1),
      ["&::placeholder"]: {
        color: tokens.placeholderColor,
        opacity: 1,
      },
      ["&.Mui-disabled"]: {
        "-webkit-text-fill-color": tokens.textColor, // Override MUI default color
      },
    },
  };

  return (
    <FormField
      {...getFormFieldProps(rest)}
      ref={ref}
      id={inputId}
      error={error}
      disabled={disabled}
      data-test={dataTest}
    >
      <Autocomplete<FormSearchableSelectOption, false, true>
        id={inputId}
        // The unmatched value is listed so MUI accepts it, then filtered out so it never shows as a choice.
        options={unmatchedOption ? [...options, unmatchedOption] : options}
        // MUI types a non-clearable value as non-null, yet null is how it renders an empty field.
        value={selectedOption as FormSearchableSelectOption}
        onChange={(_, option) => selectValue(option.value)}
        onBlur={field.onBlur}
        // Like the Select it replaces, take browser autofill: it writes text without focusing the field.
        onInputChange={(event, text, reason) => {
          const autofilledOption =
            reason === "input" && isAutofillEvent(event) ? findOptionByText(options, text) : undefined;
          if (autofilledOption && autofilledOption.value !== field.value) {
            selectValue(autofilledOption.value);
          }
        }}
        open={open}
        onOpen={(event) => {
          if (isAutofillEvent(event)) {
            return;
          }
          setOpen(true);
          onOpen?.();
        }}
        onClose={() => setOpen(false)}
        filterOptions={(candidates, state) =>
          rankOptionsByShortCode(
            candidates.filter((candidate) => candidate !== unmatchedOption),
            state
          )
        }
        isOptionEqualToValue={(option, value) => option.value === value.value}
        noOptionsText={t("common", "noOptions")}
        openText={t("common", "open")}
        closeText={t("common", "close")}
        disabled={disabled}
        // Like the Select it replaces, a picked value can be changed but never emptied.
        disableClearable
        autoHighlight
        // Matches the Select it replaces, which opened on click rather than only from the chevron.
        openOnFocus
        popupIcon={
          <FormIcon size="medium">
            <ChevronDown />
          </FormIcon>
        }
        sx={{
          width: "100%",
          // Unlike the Select's span, an input ellipsizes on sub-pixel overflow, so keep its placeholders 2px roomier.
          ["&.MuiAutocomplete-hasPopupIcon .MuiAutocomplete-inputRoot"]: {
            paddingRight: "34px",
          },
          ["& .MuiAutocomplete-endAdornment"]: {
            display: "flex",
            alignItems: "center",
            top: "50%",
            right: theme.spacing(1),
            transform: "translateY(-50%)",
          },
        }}
        // Option styles sit on the listbox itself: MUI's own option rules live there too and outrank a paper-level override.
        ListboxProps={{
          sx: {
            maxHeight: 192, // Show 5.5 elements, so users know they should scroll to see more
            scrollbarColor: `${theme.palette.formsComponents.global.scrollbarThumbColor} transparent`,
            ["&::-webkit-scrollbar-track"]: {
              background: "transparent",
            },
            ["&::-webkit-scrollbar-thumb"]: {
              background: theme.palette.formsComponents.global.scrollbarThumbColor,
            },
            ["& .MuiAutocomplete-option"]: {
              color: tokens.itemTextColor,
              transition: "background-color 100ms linear, color 100ms linear",
              minHeight: 0, // Override default MUI style
              ["&[aria-selected='true']"]: {
                backgroundColor: tokens.selectedItemBackgroundColor,
                color: tokens.selectedItemTextColor,
              },
              ["&.Mui-focused, &.Mui-focusVisible, &[aria-selected='true'].Mui-focused, &[aria-selected='true'].Mui-focusVisible"]:
                {
                  backgroundColor: tokens.itemHoverBackgroundColor,
                  color: tokens.itemTextColor,
                },
            },
          },
        }}
        slotProps={{
          popupIndicator: {
            // The Select's chevron ignored the mouse, so it never flashed a ripple when pressed.
            disableRipple: true,
            sx: {
              padding: 0,
              marginRight: 0, // Override default MUI style
              color: "inherit",
              // MUI flips the chevron instantly when the list opens; the Select it replaces turned it.
              transition: "transform 200ms ease-out",
            },
          },
          paper: {
            sx: {
              backgroundColor: tokens.popoverBackgroundColor,
              ["& .MuiAutocomplete-noOptions"]: {
                ...theme.typography.body2,
                color: tokens.placeholderColor,
              },
            },
          },
        }}
        renderOption={(props, option, { selected }) => (
          <li {...props} key={option.value} data-test={optionDataTest}>
            <FormTypography variant={selected ? "subtitle2" : "body2"} sx={{ width: "100%" }}>
              {option.label}
            </FormTypography>
          </li>
        )}
        renderInput={({ InputProps, inputProps }) => (
          <InputBase
            {...InputProps}
            inputProps={inputProps}
            inputRef={field.ref}
            name={field.name}
            placeholder={placeholder}
            disableInjectingGlobalStyles={disableInjectingGlobalStyles}
            error={error}
            disabled={disabled}
            sx={inputStyles}
          />
        )}
      />
    </FormField>
  );
}

FormSearchableSelectFieldInner.displayName = "FormSearchableSelectField";

export const FormSearchableSelectField = forwardRef(FormSearchableSelectFieldInner) as <T extends FieldValues>(
  props: FormSearchableSelectFieldProps<T> & RefAttributes<HTMLDivElement>
) => JSX.Element;

const isAutofillEvent = (event: SyntheticEvent) => event.type === "change" && event.target !== document.activeElement;

// Saved addresses hold a code ("QC") or a name ("Québec"), never our "Québec (QC)" label.
const findOptionByText = (options: FormSearchableSelectOption[], text: string) => {
  const query = normalizeAccents(text);
  if (!query) {
    return undefined;
  }
  return options.find((option) =>
    [option.value, option.label, option.shortCode, getNameWithoutShortCode(option)].some(
      (candidate) => normalizeAccents(candidate) === query
    )
  );
};

const getNameWithoutShortCode = ({ label, shortCode }: FormSearchableSelectOption) => {
  const suffix = ` (${shortCode})`;
  return shortCode && label.endsWith(suffix) ? label.slice(0, -suffix.length) : label;
};
